import { DownloadIcon } from "@chakra-ui/icons";
import { Button } from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { connect, useSelector } from "react-redux";

import { RanaHeading, RanaModal } from "@nens/ranalib";
import * as api from "@nens/threedi/openapi-client";
import { CreateTemplate } from "@nens/threedi/openapi-client";

import { getV3Api } from "../apiConfiguration";
import { AppDispatch } from "../App";
import ExportName from "../components/ExportName";
import { useOrganisationContract } from "../hooks/organisation";
import { backToStartScreen } from "../state/appState";
import { Level, inactivateModal, sendMessage } from "../state/mainGui";
import { getUser } from "../state/session";
import {
  getDownloadSimulationResults,
  getSimulation,
  getThreediModelNotNull,
  requestDownloadSimulationResults,
  shutdownSimulation,
} from "../state/simulation";
import { getSelectedOrganisation } from "../state/simulation";
import { getCurrentTimestamp } from "../utils/timeConverter";

import ExportCreateSimulationTemplate from "./exportmenu/ExportCreateSimulationTemplate";
import ExportDamageEstimation from "./exportmenu/ExportDamageEstimation";
import ExportRow from "./exportmenu/ExportRow";
import ExportRowMain from "./exportmenu/ExportRowMain";
import styles from "./ExportMenu.module.css";

// Helper function to ensure no special character is allowed in export name
export const removeSpecialCharactersFromString = (name: string) => {
  // List of special characters not allowed in Windows and Linux directory names:
  // https://stackoverflow.com/questions/1976007/what-characters-are-forbidden-in-windows-and-linux-directory-names
  const stringWithoutSpecialCharacters = name.replace(/[<>:"/\\|?*]/g, "");
  return stringWithoutSpecialCharacters;
};

const ExportMenu: React.FC<Props> = (props) => {
  const intl = useIntl();
  const simulation = useSelector(getSimulation);
  const threediModel = useSelector(getThreediModelNotNull);
  const user = useSelector(getUser);
  const username = user && user.username;
  const downloadSimulationResults = useSelector(getDownloadSimulationResults);

  const [apiError, setApiError] = useState(false);
  const [rawResultOption, setRawResultOption] = useState(false);
  const [basicOption, setBasicOption] = useState(false);
  const [arrivalOption, setArrivalOption] = useState(false);
  const [damageOption, setDamageOption] = useState(false);
  const [damageParameterCostType, setDamageParameterCostType] = useState<api.CostTypeEnum>(
    api.CostTypeEnum.AVG
  );
  const [damageParameterFloodMonth, setDamageParameterFloodMonth] = useState<api.FloodMonthEnum>(
    api.FloodMonthEnum.SEP
  );
  const [damageParameterInundationPeriod, setDamageParameterInundationPeriod] = useState(1);
  const [damageParameterRepairTimeBuildings, setDamageParameterRepairTimeBuildings] = useState(24);
  const [damageParameterRepairTimeInfrastructure, setDamageParameterRepairTimeInfrastructure] =
    useState(6);
  const [retrievedBackendValues, setRetrievedBackendValues] = useState(false);

  const [saveSimulationTemplate, setSaveSimulationTemplate] = useState(false);
  const [simulationTemplate, setSimulationTemplate] = useState<CreateTemplate>({
    // Note the swagger doc says a URL should be passed as simulation --
    // but we need to pass an id as string.
    simulation: "" + simulation.id!,
    name: "",
    includeEvents: true,
    includeInitials: true,
    includeSettings: true,
  });

  // Helper function to ensure default export name cannot exceed the maximal characters allowed
  const maxExportNameLength = 50;
  const validateExportName = (exportName: string) => {
    if (exportName.length > maxExportNameLength) {
      return exportName.slice(0, maxExportNameLength);
    } else {
      return exportName;
    }
  };

  // Set Lizard export name in the naming screen
  const defaultExportName = threediModel.name
    ? `${getCurrentTimestamp()}_${threediModel.name}_${username}`
    : `${username}_${getCurrentTimestamp()}`;
  const [exportName, setExportName] = useState<string>(
    validateExportName(removeSpecialCharactersFromString(defaultExportName))
  );
  const [namingScreen, showNamingScreen] = useState(false);

  const v3Api = getV3Api();

  const organisation = useSelector(getSelectedOrganisation);
  const contract = useOrganisationContract(organisation ? organisation.uniqueId : "");

  const hasLizardProcessScope = contract?.scope?.includes("lizardprocess");

  useEffect(() => {
    if (!retrievedBackendValues) {
      setRetrievedBackendValues(true);

      if (simulation === null) return;
      const exportSimulationId = simulation.id;
      if (exportSimulationId === undefined) return;

      v3Api
        .simulationsResultsPostProcessingLizardBasicList({
          simulationPk: exportSimulationId,
        })
        .then((response) => {
          setRawResultOption(true);
          setBasicOption(!!response.processBasicResults);
        })
        .catch(() => {
          setRawResultOption(false);
        });

      v3Api
        .simulationsResultsPostProcessingLizardArrivalList({
          simulationPk: exportSimulationId,
        })
        .then(() => {
          setArrivalOption(true);
        })
        .catch(() => {
          setArrivalOption(false);
        });

      v3Api
        .simulationsResultsPostProcessingLizardDamageList({
          simulationPk: exportSimulationId,
        })
        .then((response) => {
          setDamageOption(true);
          response.costType && setDamageParameterCostType(response.costType);
          response.floodMonth && setDamageParameterFloodMonth(response.floodMonth);
          response.inundationPeriod &&
            setDamageParameterInundationPeriod(response.inundationPeriod);
          response.repairTimeBuildings &&
            setDamageParameterRepairTimeBuildings(response.repairTimeBuildings);
          response.repairTimeInfrastructure &&
            setDamageParameterRepairTimeInfrastructure(response.repairTimeInfrastructure);
        })
        .catch(() => {
          setDamageOption(false);
        });
    }
  }, [simulation, v3Api, retrievedBackendValues]);

  const deleteBasicExportPromise = async () => {
    if (simulation === null) {
      console.error("Why is simulation null !?!");
      return Promise.reject("simulation === null");
    }
    const deleteSimulationId = simulation.id;
    if (deleteSimulationId === undefined) return Promise.reject("simulation.id === undefined");
    return v3Api
      .simulationsResultsPostProcessingLizardBasicDelete({
        simulationPk: deleteSimulationId,
      })
      .catch((error) => {
        console.error(
          "error deleting basic-request. This is probably not a real problem, but simply a delete while the basic settings didnot yet exist: ",
          error
        );
      });
  };

  const createExportBasicRecordIfNeeded = async () => {
    if (rawResultOption) {
      if (simulation === null) return;
      const basicSimulationId = simulation.id;
      if (basicSimulationId === undefined) return;
      const simulationsResultsPostProcessingLizardBasicCreateRequest = {
        simulationPk: basicSimulationId,
        data: {
          scenarioName: exportName,
          processBasicResults: basicOption,
        },
      };
      return v3Api.simulationsResultsPostProcessingLizardBasicCreate(
        simulationsResultsPostProcessingLizardBasicCreateRequest
      );
    }
  };

  const createExportArrivalRecordIfNeeded = async () => {
    if (arrivalOption) {
      if (simulation === null) return;
      const arrivalSimulationId = simulation.id;
      if (arrivalSimulationId === undefined) return;
      const simulationsResultsPostProcessingLizardArrivalCreateRequest = {
        simulationPk: arrivalSimulationId,
        data: {},
      };
      return v3Api.simulationsResultsPostProcessingLizardArrivalCreate(
        simulationsResultsPostProcessingLizardArrivalCreateRequest
      );
    }
  };
  const createExportDamageRecordIfNeeded = async () => {
    if (damageOption) {
      if (simulation === null) return;
      const damageSimulationId = simulation.id;
      if (damageSimulationId === undefined) return;
      const simulationsResultsPostProcessingLizardDamageCreateRequest = {
        simulationPk: damageSimulationId,
        data: {
          costType: damageParameterCostType,
          floodMonth: damageParameterFloodMonth,
          inundationPeriod: damageParameterInundationPeriod,
          repairTimeBuildings: damageParameterRepairTimeBuildings,
          repairTimeInfrastructure: damageParameterRepairTimeInfrastructure,
        },
      };
      return v3Api.simulationsResultsPostProcessingLizardDamageCreate(
        simulationsResultsPostProcessingLizardDamageCreateRequest
      );
    }
  };

  const canCreateSimulationTemplate =
    saveSimulationTemplate &&
    simulationTemplate.name &&
    (simulationTemplate.includeEvents ||
      simulationTemplate.includeInitials ||
      simulationTemplate.includeSettings);

  const createSimulationTemplate = async () => {
    const v3Api = getV3Api();

    await v3Api.simulationTemplatesCreate({
      data: simulationTemplate,
    });

    props.sendMessage(`Created new simulation template "${simulationTemplate.name}".`);
  };

  // This is the actual export!
  const clickExportButton = () => {
    // Part 1 - exports of files
    if (exportName) {
      (async () => {
        // delete the basic export will also cause the arrival and damage exports to be deleted
        try {
          await deleteBasicExportPromise();
        } catch (error) {
          console.error("saving export failed", error);
          setApiError(true);
        } finally {
          // finally always executes. Either on succeed and on fail.
          // we do this because we always do a delete, even if there is nothing to delete, therefore the delete sometimes fails
          // we thus proceed the promise chain in the finally clause
          await createExportBasicRecordIfNeeded();
          await createExportArrivalRecordIfNeeded();
          await createExportDamageRecordIfNeeded();
          props.shutdownSimulation();
          // if download option is not selected, go back to start screen
          if (!downloadSimulationResults) {
            props.backToStartScreen();
          }
        }
      })();
    }

    // Part 2 - create a simulation template
    if (canCreateSimulationTemplate) {
      createSimulationTemplate();
    }
  };

  // At least one of the below options must be selected in order to store and/or export results
  const buttonNotAllowed =
    !downloadSimulationResults && !rawResultOption && !canCreateSimulationTemplate;

  return (
    <RanaModal
      isOpen={true}
      topBar={
        namingScreen ? (
          <FormattedMessage id="exportMenu.export_name" defaultMessage="Export name" />
        ) : (
          <FormattedMessage
            id="exportMenu.results_selection"
            defaultMessage="Select results to be stored"
          />
        )
      }
      onClose={props.inactivateModal}
      buttons={[
        <Button
          variant="secondary"
          onClick={() => {
            props.inactivateModal();
          }}
        >
          <FormattedMessage id="exportMenu.cancel_button" defaultMessage="cancel" />
        </Button>,

        namingScreen ? (
          <Button
            variant="primary"
            onClick={clickExportButton}
            title={
              !exportName
                ? intl.formatMessage({
                    id: "exportMenu.export_button_disabled",
                    defaultMessage: "Please enter a unique name for the export",
                  })
                : ""
            }
            disabled={!exportName}
          >
            <FormattedMessage id="exportMenu.export_and_quit" defaultMessage="Export and Quit" />
          </Button>
        ) : (
          <Button
            variant="primary"
            onClick={() => {
              if (rawResultOption) {
                showNamingScreen(true); // Open naming screen for Lizard export
              } else {
                if (canCreateSimulationTemplate) {
                  createSimulationTemplate();
                }
                // No export option is selected
                props.shutdownSimulation();
                if (!downloadSimulationResults) props.backToStartScreen();
              }
            }}
            title={
              buttonNotAllowed
                ? intl.formatMessage({
                    id: "exportMenu.store_button_disabled",
                    defaultMessage: "Please select at least one option",
                  })
                : ""
            }
            isDisabled={buttonNotAllowed}
          >
            <FormattedMessage id="exportMenu.store_results" defaultMessage="Store results" />
          </Button>
        ),
      ]}
    >
      {namingScreen ? (
        <ExportName
          exportName={exportName}
          setExportName={setExportName}
          maxExportNameLength={maxExportNameLength}
        />
      ) : (
        <div className={styles.ExportMenu}>
          <ExportRow>
            <ExportRowMain
              selected={downloadSimulationResults}
              onClick={props.requestDownloadSimulationResults}
              exportImage={{
                src: "./assets/images/export-raw.png",
                alt: "Raw results",
              }}
              tag={
                <>
                  <DownloadIcon />
                  <FormattedMessage
                    id="export_modal.export_tag_direct_download"
                    defaultMessage="Direct download"
                  />
                </>
              }
            >
              <RanaHeading type="h5" fontWeight="semibold" mb="8px">
                <FormattedMessage
                  id="export_modal.export_text_raw_title"
                  defaultMessage="Raw data and logging"
                />
              </RanaHeading>
              <p>
                <FormattedMessage
                  id="export_modal.export_text_raw_content"
                  defaultMessage="NetCDF and logging"
                />
              </p>
            </ExportRowMain>
          </ExportRow>
          <hr className={styles.SeparatorLine} />
          <ExportRow
            title={
              !hasLizardProcessScope
                ? intl.formatMessage({
                    id: "error.not_sufficient_right",
                    defaultMessage:
                      "You do not have sufficient right to perform this request. Please contact the Service Desk for more information.",
                  })
                : ""
            }
          >
            <ExportRowMain
              selected={rawResultOption}
              disabled={!hasLizardProcessScope}
              onClick={() => {
                setRawResultOption(!rawResultOption);
                if (rawResultOption) {
                  setBasicOption(false);
                  setArrivalOption(false);
                  setDamageOption(false);
                }
              }}
              exportImage={{
                src: "./assets/images/export-raw.png",
                alt: "Raw results",
              }}
              tag={
                <FormattedMessage
                  id="export_modal.export_tag_to_lizard"
                  defaultMessage="Export to Lizard"
                />
              }
            >
              <RanaHeading type="h5" fontWeight="semibold" mb="8px">
                <FormattedMessage
                  id="export_modal.export_text_raw_title"
                  defaultMessage="Raw data and logging"
                />
              </RanaHeading>
              <p>
                <FormattedMessage
                  id="export_modal.export_text_raw_content"
                  defaultMessage="NetCDF and logging"
                />
              </p>
            </ExportRowMain>
          </ExportRow>
          <ExportRow>
            <ExportRowMain
              selected={basicOption}
              disabled={!rawResultOption}
              onClick={() => {
                if (rawResultOption) {
                  setBasicOption(!basicOption);
                  if (basicOption) {
                    setBasicOption(false);
                    setArrivalOption(false);
                    setDamageOption(false);
                  } else {
                    setBasicOption(true);
                  }
                }
              }}
              exportImage={{
                src: "./assets/images/export-basic.png",
                alt: "Basic processed results",
              }}
              tag={
                <FormattedMessage
                  id="export_modal.export_tag_to_lizard"
                  defaultMessage="Export to Lizard"
                />
              }
            >
              <RanaHeading type="h5" fontWeight="semibold" mb="8px">
                <FormattedMessage
                  id="export_modal.export_text_basic_title"
                  defaultMessage="Basic processed results"
                />
              </RanaHeading>
              <p>
                <FormattedMessage
                  id="export_modal.export_text_basic_content"
                  defaultMessage="NetCDF, logging, water depth and waterlevels"
                />
              </p>
            </ExportRowMain>
          </ExportRow>
          <div className={styles.ExportExtraOptions}>
            <ExportRow>
              <ExportRowMain
                selected={arrivalOption}
                disabled={!basicOption}
                onClick={() => {
                  if (basicOption) {
                    setArrivalOption(!arrivalOption);
                  }
                }}
                exportImage={{
                  src: "./assets/images/export-arrival.png",
                  alt: "Arrival time map",
                }}
              >
                <RanaHeading type="h5" fontWeight="semibold" mb="8px">
                  <FormattedMessage
                    id="export_modal.export_text_arrival_title"
                    defaultMessage="Arrival time map"
                  />
                </RanaHeading>
                <p>
                  <FormattedMessage
                    id="export_modal.export_text_arrival_content"
                    defaultMessage="Basic processed results and arrival time map"
                  />
                </p>
              </ExportRowMain>
            </ExportRow>
            <ExportDamageEstimation
              damageEstimation={damageOption}
              setDamageEstimation={setDamageOption}
              basicOption={basicOption}
              costType={damageParameterCostType}
              floodMonth={damageParameterFloodMonth}
              inundationPeriod={damageParameterInundationPeriod}
              repairTimeBuildings={damageParameterRepairTimeBuildings}
              repairTimeInfrastructure={damageParameterRepairTimeInfrastructure}
              setDamageCostType={setDamageParameterCostType}
              setDamageFloodMonth={setDamageParameterFloodMonth}
              setDamageInundation={setDamageParameterInundationPeriod}
              setDamageBuildings={setDamageParameterRepairTimeBuildings}
              setDamageInfrastructure={setDamageParameterRepairTimeInfrastructure}
              damageOptionChecked={damageOption}
            />
          </div>
          <hr className={styles.SeparatorLine} />
          <ExportCreateSimulationTemplate
            selected={saveSimulationTemplate}
            setSelected={(selected: boolean) => setSaveSimulationTemplate(selected)}
            createTemplateRequest={simulationTemplate}
            updateCreateTemplateRequest={(request) =>
              setSimulationTemplate({ ...simulationTemplate, ...request })
            }
          />
          {apiError ? (
            <span style={{ color: "red" }}>
              <FormattedMessage
                id="export_modal.saving_failed"
                defaultMessage="Something went wrong while setting storage options"
              />
            </span>
          ) : null}
        </div>
      )}
    </RanaModal>
  );
};

const mapDispatchToProps = (dispatch: AppDispatch) => {
  return {
    inactivateModal: () => dispatch(inactivateModal()),
    shutdownSimulation: () => dispatch(shutdownSimulation()),
    backToStartScreen: () => dispatch(backToStartScreen()),
    requestDownloadSimulationResults: () => dispatch(requestDownloadSimulationResults()),
    sendMessage: (message: string | JSX.Element, timeoutMs = 5000, level: Level = "info") =>
      dispatch(sendMessage(message, timeoutMs, level)),
  };
};
type Props = ReturnType<typeof mapDispatchToProps>;

export default connect(null, mapDispatchToProps)(ExportMenu);
